import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Skeleton } from '@mui/material';

import Button from '@components/button/Button';
import Checkbox from '@components/checkbox/Checkbox';
import {
  DROPDOWN_VARIANTS,
  resolvePortalPanelClassName
} from '@components/drop-down/Dropdown';
import PlusNChipList from '@components/plus-n-chip-list/PlusNChipList';
import SearchBar from '@components/searchbar/SearchBar';
import useOnClickOutside from '@utils/hooks';
import { DropdownOption, MultiSelectDropdownV2Props } from 'types/dropdown';

const PORTAL_GAP = 4;
const VISIBLE_LIST_ITEMS = 4;
const ROW_HEIGHT = 40;
const LIST_MAX_HEIGHT = VISIBLE_LIST_ITEMS * ROW_HEIGHT;
const PANEL_CHROME_HEIGHT = 108;

const estimatePanelHeight = (hasSearchBar: boolean) =>
  PANEL_CHROME_HEIGHT + (hasSearchBar ? 56 : 0) + LIST_MAX_HEIGHT;

const computePanelPosition = (rect: DOMRectReadOnly, panelHeight: number) => {
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom - PORTAL_GAP;
  const spaceAbove = rect.top - PORTAL_GAP;
  const shouldOpenUp = spaceBelow < panelHeight && spaceAbove > spaceBelow;

  return shouldOpenUp
    ? {
        left: rect.left,
        width: rect.width,
        top: 0,
        bottom: viewportHeight - rect.top + PORTAL_GAP
      }
    : {
        left: rect.left,
        width: rect.width,
        top: rect.bottom + PORTAL_GAP,
        bottom: null as number | null
      };
};

const SELECTED_CHIP_CLASS =
  '!h-auto !rounded-lg !bg-cofeeGreyBlue75 !px-3 !py-1.5 text-sm font-normal text-cofeeNeutral10';

const findOptionIndex = (options: DropdownOption[], item: DropdownOption) =>
  options.findIndex((option) => option.id === item.id);

const MultiSelectDropdownV2 = ({
  label,
  searchBarPlaceholder,
  options,
  selectedOptions,
  onApply,
  className,
  buttonClassName,
  isLoading,
  usePortal = false,
  displayChipsCount = 2,
  isChipCloseIconRequired = true,
  minSelectCount = 0,
  testId = 'multiselect-dropdown-v2',
  plusMoreClickRequired = true
}: MultiSelectDropdownV2Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stagedOptions, setStagedOptions] =
    useState<DropdownOption[]>(selectedOptions);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    bottom: null as number | null
  });
  const [isPositionReady, setIsPositionReady] = useState(false);

  const variantStyles = DROPDOWN_VARIANTS.v2;
  const resolvedButtonClassName =
    buttonClassName ?? variantStyles.buttonClassName;
  const portalPanelClassName = resolvePortalPanelClassName('v2');

  const filteredOptions = searchTerm
    ? options.filter((option) =>
        option.label?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const stagedInFilteredCount = filteredOptions.filter(
    (option) => findOptionIndex(stagedOptions, option) >= 0
  ).length;

  const isAllFilteredSelected =
    filteredOptions.length > 0 &&
    stagedInFilteredCount === filteredOptions.length;

  const isSelectAllIndeterminate =
    stagedInFilteredCount > 0 && !isAllFilteredSelected;

  const closePanel = () => {
    setIsOpen(false);
    setSearchTerm('');
    setStagedOptions(selectedOptions);
  };

  const openPanel = () => {
    setStagedOptions(selectedOptions);
    setSearchTerm('');
    setIsOpen(true);
    if (usePortal) {
      setIsPositionReady(false);
    }
  };

  const togglePanel = () => {
    if (isOpen) {
      closePanel();
      return;
    }
    openPanel();
  };

  const handleApply = () => {
    onApply(stagedOptions);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    setStagedOptions([]);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const missing = filteredOptions.filter(
        (option) => findOptionIndex(stagedOptions, option) < 0
      );
      setStagedOptions([...stagedOptions, ...missing]);
      return;
    }
    setStagedOptions(
      stagedOptions.filter(
        (selected) =>
          !filteredOptions.some((filtered) => filtered.id === selected.id)
      )
    );
  };

  const handleOptionChange = (
    option: DropdownOption,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      if (findOptionIndex(stagedOptions, option) < 0) {
        setStagedOptions([...stagedOptions, option]);
      }
      return;
    }
    setStagedOptions(
      stagedOptions.filter((selected) => selected.id !== option.id)
    );
  };

  const handleChipRemove = (option: DropdownOption) => {
    const updated = selectedOptions.filter((item) => item.id !== option.id);
    onApply(updated);
  };

  useOnClickOutside(usePortal ? panelRef : ref, (event) => {
    if (usePortal && ref.current?.contains(event.target as Node)) {
      return;
    }
    event.stopPropagation();
    closePanel();
  });

  useEffect(() => {
    if (!isOpen) {
      setStagedOptions(selectedOptions);
    }
  }, [isOpen, selectedOptions]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSelectAllIndeterminate;
    }
  }, [isSelectAllIndeterminate, isOpen]);

  const updatePortalPosition = useCallback(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const panelHeight =
      panelRef.current?.offsetHeight ??
      estimatePanelHeight(Boolean(searchBarPlaceholder));

    setPosition(computePanelPosition(rect, panelHeight));
    setIsPositionReady(true);
  }, [searchBarPlaceholder]);

  useLayoutEffect(() => {
    if (!usePortal || !isOpen) {
      return;
    }
    updatePortalPosition();
  }, [isOpen, usePortal, updatePortalPosition, stagedOptions.length]);

  useEffect(() => {
    if (!usePortal || !isOpen) {
      return undefined;
    }

    window.addEventListener('scroll', updatePortalPosition, true);
    window.addEventListener('resize', updatePortalPosition);

    return () => {
      window.removeEventListener('scroll', updatePortalPosition, true);
      window.removeEventListener('resize', updatePortalPosition);
    };
  }, [isOpen, usePortal, updatePortalPosition]);

  if (isLoading) {
    return (
      <div className={className || 'w-full'}>
        <Skeleton
          height={44}
          animation="wave"
          variant="rounded"
          className="!bg-cofeeGreyBlue100"
        />
      </div>
    );
  }

  const renderPanelContent = () => (
    <>
      {searchBarPlaceholder && (
        <div className="px-2 pb-4 pt-2">
          <SearchBar
            placeholder={searchBarPlaceholder}
            onSearch={setSearchTerm}
            value={searchTerm}
          />
        </div>
      )}
      {filteredOptions.length > 0 ? (
        <>
          <div className="-mx-2 border-y border-cofeeGreyBlue200">
            <div className="p-4">
              <Checkbox
                ref={selectAllRef}
                handleChange={handleSelectAll}
                label="Select all"
                isChecked={isAllFilteredSelected}
                className="!w-full"
                labelStyle="!text-cofeeNeutral10 !text-base !font-normal"
              />
            </div>
          </div>
          <div className="custom-scroll max-h-40 overflow-y-auto px-2 py-1">
            {filteredOptions.map((option) => (
              <Checkbox
                key={option.id}
                handleChange={(event) => handleOptionChange(option, event)}
                label={option.label}
                isChecked={findOptionIndex(stagedOptions, option) >= 0}
                className="!w-full py-2"
                labelStyle="!text-cofeeNeutral10 !text-base !font-normal"
              />
            ))}
          </div>
          <div className="-mx-2 border-t border-cofeeGreyBlue300 px-4 pb-3 pt-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-cofeeNeutral8">
                {stagedOptions.length} of {options.length} selected
              </span>
              <div className="flex w-2/5 items-center gap-2">
                <Button
                  label="Clear"
                  variant="tertiary"
                  size="small"
                  handleClick={handleClear}
                  className="!h-10 flex-1"
                />
                <Button
                  label="Apply"
                  variant="primary"
                  size="small"
                  handleClick={handleApply}
                  disabled={stagedOptions.length < minSelectCount}
                  className="!h-10 flex-1"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-24 items-center justify-center px-4 text-sm text-cofeeNeutral8">
          No results found
        </div>
      )}
    </>
  );

  const panelClassName = `rounded-md border border-grey40 bg-theme px-2 shadow-sm ${portalPanelClassName}`;

  return (
    <div ref={ref} className={`relative w-full ${className || ''}`}>
      <Button
        data-testid={testId}
        variant="tertiary"
        handleClick={togglePanel}
        size="medium"
        label={
          selectedOptions.length ? (
            <PlusNChipList
              selectedOptions={selectedOptions}
              handleOnRemove={handleChipRemove}
              displayChipsCount={displayChipsCount}
              isCloseIconRequired={isChipCloseIconRequired}
              chipClassName={SELECTED_CHIP_CLASS}
              tooltipChipClassName={SELECTED_CHIP_CLASS}
              plusNChipBgColor="bg-cofeeGreyBlue75"
              plusMoreClickRequired={plusMoreClickRequired}
              plusNChipTextColor="text-cofeePrimary6"
              plusNChipClassName="!rounded-lg"
            />
          ) : (
            label
          )
        }
        rightIconName="down-icon-dark"
        className={`w-full justify-between border ${resolvedButtonClassName}`}
        labelStyle={
          selectedOptions.length
            ? '!font-normal'
            : `!text-base !font-normal ${variantStyles.placeholderStyle}`
        }
        rightIconStyle={isOpen ? '-rotate-180' : ''}
      />
      {isOpen &&
        usePortal &&
        createPortal(
          <div
            ref={panelRef}
            className={`fixed z-[9999] ${panelClassName} ${
              isPositionReady ? '' : 'invisible'
            }`}
            style={{
              top: position.bottom != null ? 'auto' : `${position.top}px`,
              bottom: position.bottom != null ? `${position.bottom}px` : 'auto',
              left: `${position.left}px`,
              minWidth: `${position.width}px`
            }}
            role="presentation"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {renderPanelContent()}
          </div>,
          document.body
        )}
      {isOpen && !usePortal && (
        <div
          ref={panelRef}
          className={`absolute left-0 z-20 mt-1 w-full ${panelClassName} ${variantStyles.optionsStyle}`}
          role="presentation"
        >
          {renderPanelContent()}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdownV2;
